import React, { useEffect, useRef } from "react";

import {
  PLAYBACK_API,
  PLAYBACK_SPEEDS,
  getSegStartSec,
  secToDateTimeString,
} from "../../utils/playbackUtils";
import { getEventOwnerLine } from "../../utils/ownerLine";

// 하루 기준 초 → "HH:MM:SS"
function secToClock(sec) {
  const s = Math.max(0, Math.floor(Number(sec) || 0));
  const h = String(Math.floor(s / 3600)).padStart(2, "0");
  const m = String(Math.floor((s % 3600) / 60)).padStart(2, "0");
  const ss = String(s % 60).padStart(2, "0");
  return `${h}:${m}:${ss}`;
}

// "HH:MM" / "HH:MM:SS" → 초
function clockToSec(t) {
  const p = String(t || "").split(":").map((x) => parseInt(x, 10) || 0);
  return (p[0] || 0) * 3600 + (p[1] || 0) * 60 + (p[2] || 0);
}

// 이 구간부터 조회 종료시각까지 몇 초를 이어서 받을지.
//   한 번에 너무 길게 잡으면 서버가 오래 붙들고 있으므로 상한을 둔다(서버도 상한이 있다).
function remainingSec(date, seg, endTime) {
  const from = getSegStartSec(seg);
  const to = clockToSec(endTime || "23:59:59");
  const left = Math.max(60, to - from);
  return Math.min(left, 3600);
}

// ── 다시보기 영상 경로 ───────────────────────────────────────────
//   기본은 <되감기 되는 경로>다. 고객은 monity.co.kr 에서 다시보기로 들어올 뿐이므로
//   주소에 뭘 붙이게 할 수 없다.
//
//   왜 바꿨나 (2026-09-30 실측)
//     예전 경로는 1시간치 약 300MB 를 통째로 내보내면서 'Accept-Ranges: none' 으로 답했다.
//     크롬은 매 요청마다 'Range: bytes=0-' 로 부분 요청이 되는지 묻는데 안 된다는 답을 받으므로,
//     버퍼가 차서 이미 본 구간을 버리면 <다시 달라고 할 수단이 없어> 재생이 멈췄다.
//     멈춘 순간 실측: 브라우저 요청 0건 + 서버 연결 0개 (누가 느려서가 아니라 아무도 요청을 못 함).
//     지금 경로는 서버가 영상을 파일로 받아둔 뒤 내보내므로 206 부분 요청이 되고, 되감기도 된다.
//     대신 첫 요청에 준비 시간이 붙는다(1시간치 약 2.5초). 같은 구간 재요청은 캐시라 즉시.
//
//   ?legacy — 예전 경로로 되돌리는 비상구. 고객은 쓸 일이 없다.
//     문제가 생겼을 때 <배포 없이> 두 방식을 비교하려고 남겨둔다.
//     예전 경로(/api/playback/stream)도 서버에 그대로 살아 있다.
//
//   이 코드베이스는 라우터를 쓰지 않고 window.location 을 직접 읽는다(App.js 와 같은 방식).
const USE_LEGACY = (() => {
  try {
    return new URLSearchParams(window.location.search).has("legacy");
  } catch (_) {
    return false;
  }
})();
const STREAM_PATH = USE_LEGACY
  ? "/api/playback/stream"
  : "/api/playback/stream-seekable";

export default function PlaybackVideoPanel({
  selectedCamera,
  selectedDate,
  startTime,
  endTime,

  selectedSegment,
  videoRef,
  playbackRate,
  onPlaybackRateChange,

  onFirst,
  onPrev,
  onNext,
  onLast,
  onDownload,

  playheadSec,
  playStartOffsetSec = 0,
  onPlayheadUpdate,
}) {
  // ── 끊김 없는 재생 ──────────────────────────────────────────────
  //   녹화는 15초씩 잘려 저장된다. 조각을 하나씩 불러 재생하면
  //   15초마다 <새 파일을 처음부터 받는다>.
  //   서버에서 보면 티가 안 나지만, 외부(휴대폰·LTE)에서는 그때마다
  //   왕복 지연이 붙어 15초마다 멈칫한다.
  //   → 서버가 조각을 이어붙여 한 덩어리로 주는 경로를 쓴다.
  //   그 경로가 아직 준비 안 된 서버에서는 예전 방식(조각 재생)으로 내려간다.
  const [seamlessFailed, setSeamlessFailed] = React.useState(false);
  //   selectedCamera 는 객체다. 서버에는 id 를 보낸다.
  const cameraId = selectedCamera && (selectedCamera.id ?? selectedCamera.camIdx);
  //   ※ PLAYBACK_API(/__playback_proxy__)를 붙이면 안 된다.
  //     그건 mintcam-server 로 넘기는 통로이고, 이 경로는 <대시보드 서버 자신>에 있다.
  //     붙이면 404 가 나고 조용히 조각 재생으로 폴백된다(2026-08-27 실제로 그랬다).
  const seamlessSrc =
    !seamlessFailed && cameraId && selectedDate && selectedSegment
      ? STREAM_PATH +
        `?cameraId=${encodeURIComponent(cameraId)}` +
        `&start=${encodeURIComponent(
          `${selectedDate}T${secToClock(getSegStartSec(selectedSegment))}`
        )}` +
        `&duration=${encodeURIComponent(remainingSec(selectedDate, selectedSegment, endTime))}`
      : "";

  const fallbackSrc = selectedSegment?.playbackUrl
    ? `${PLAYBACK_API}${selectedSegment.playbackUrl}`
    : "";

  const videoSrc = seamlessSrc || fallbackSrc;

  // 현재 파일을 "몇 초부터" 재생할지(타임라인 클릭 시크). 로드 완료 시점에 반영.
  const startOffsetRef = useRef(playStartOffsetSec);
  startOffsetRef.current = playStartOffsetSec;

  // 라이브 플레이헤드(빨간 선)를 위해 현재 파일의 시작 시각(초)을 참조.
  const segStartRef = useRef(0);
  segStartRef.current = selectedSegment ? getSegStartSec(selectedSegment) : 0;

  // 구간이 바뀌면 같은 <video> 엘리먼트에 영상만 갈아끼워 로드하고,
  // 클릭한 지점(오프셋)부터 재생. (엘리먼트를 새로 만들지 않으므로 전체화면 유지)
  useEffect(() => {
    const v = videoRef?.current;
    if (!v || !videoSrc) return;

    const onMeta = () => {
      const off = startOffsetRef.current;
      if (off > 0 && off < (v.duration || Infinity)) {
        try {
          v.currentTime = off;
        } catch (err) {}
      }
      const p = v.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    };

    v.addEventListener("loadedmetadata", onMeta);
    v.load();

    return () => v.removeEventListener("loadedmetadata", onMeta);
  }, [videoSrc, videoRef]);

  // 재생 중 빨간 플레이헤드가 실시간으로 이동하도록(초 단위로만 갱신).
  useEffect(() => {
    const v = videoRef?.current;
    if (!v || typeof onPlayheadUpdate !== "function") return;

    let lastSec = -1;
    const onTime = () => {
      const sec = Math.floor(segStartRef.current + (v.currentTime || 0));
      if (sec !== lastSec) {
        lastSec = sec;
        onPlayheadUpdate(sec);
      }
    };

    v.addEventListener("timeupdate", onTime);
    return () => v.removeEventListener("timeupdate", onTime);
  }, [videoRef, onPlayheadUpdate]);

  const playheadText =
    playheadSec != null
      ? secToDateTimeString(selectedDate, playheadSec)
      : "-";

  const selectedFileText = selectedSegment?.fileName || "-";

  return (
    <div className="flex min-h-0 flex-col rounded-2xl bg-white p-3 shadow-md dark:bg-[#1F2A3B] lg:flex-[1.9]">
      <div className="mb-2 grid shrink-0 grid-cols-1 gap-2 xl:grid-cols-[minmax(180px,0.9fr)_minmax(180px,1fr)_minmax(220px,1.3fr)_auto] xl:items-center">
        <div className="min-w-0">
          <h4 className="text-base font-bold sm:text-lg">영상 재생</h4>
          <div className="break-words text-sm text-gray-500 dark:text-gray-400">
            {/* "지점 / 팀 / 이름 / 차량번호".
                값이 하나도 없으면 기존 표기로 돌아간다. */}
            {selectedCamera
              ? getEventOwnerLine(selectedCamera) ||
                `${selectedCamera.camName}${
                  selectedCamera.camNo ? ` / CAM ${selectedCamera.camNo}` : ""
                }`
              : "카메라를 선택해주세요."}
          </div>
        </div>

        <div className="min-w-0 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 dark:border-gray-700 dark:bg-[#0F172A]">
          <div className="text-[11px] text-gray-500 dark:text-gray-400">
            플레이헤드 시간
          </div>
          <div className="truncate text-sm font-semibold text-gray-900 dark:text-white">
            {playheadText}
          </div>
        </div>

        <div className="min-w-0 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 dark:border-gray-700 dark:bg-[#0F172A]">
          <div className="text-[11px] text-gray-500 dark:text-gray-400">
            선택 파일
          </div>
          <div className="truncate text-sm font-semibold text-gray-900 dark:text-white">
            {selectedFileText}
          </div>
        </div>

        <div className="shrink-0 text-xs text-gray-500 dark:text-gray-400 xl:text-right">
          {selectedDate} / {startTime} ~ {endTime}
        </div>
      </div>

      <div className="min-h-[220px] flex-1 overflow-hidden rounded-xl bg-black lg:min-h-0">
        {selectedSegment ? (
          <video
            ref={videoRef}
            src={videoSrc}
            controls
            playsInline
            // 이어붙인 영상은 끝나면 그것으로 끝이다. 조각 재생일 때만 다음 조각으로 넘긴다.
            onEnded={seamlessSrc ? undefined : onNext}
            // 이어붙이기 경로가 안 되는 서버(구버전)면 조각 재생으로 내려간다.
            onError={() => {
              if (seamlessSrc && !seamlessFailed) {
                console.warn("[다시보기] 이어붙이기 실패 → 조각 재생으로 전환");
                setSeamlessFailed(true);
              }
            }}
            className="h-full min-h-[220px] w-full bg-black object-contain lg:min-h-0"
          />
        ) : (
          <div className="flex h-full min-h-[220px] items-center justify-center px-4 text-center text-sm text-gray-300 lg:min-h-0">
            조회 후 타임라인의 녹화 구간을 클릭하면 재생됩니다.
          </div>
        )}
      </div>

      <div className="mt-2 flex shrink-0 flex-col gap-2 xl:flex-row xl:items-center xl:justify-between">
        <div className="grid grid-cols-4 gap-2 lg:flex lg:flex-wrap lg:items-center">
          <button
            type="button"
            onClick={onFirst}
            className="rounded-xl bg-gray-200 px-2 py-2 text-sm font-semibold text-gray-800 dark:bg-gray-700 dark:text-white lg:px-3"
          >
            처음
          </button>

          <button
            type="button"
            onClick={onPrev}
            className="rounded-xl bg-gray-200 px-2 py-2 text-sm font-semibold text-gray-800 dark:bg-gray-700 dark:text-white lg:px-3"
          >
            이전
          </button>

          <button
            type="button"
            onClick={onNext}
            className="rounded-xl bg-blue-600 px-2 py-2 text-sm font-semibold text-white hover:bg-blue-700 lg:px-3"
          >
            다음
          </button>

          <button
            type="button"
            onClick={onLast}
            className="rounded-xl bg-gray-200 px-2 py-2 text-sm font-semibold text-gray-800 dark:bg-gray-700 dark:text-white lg:px-3"
          >
            끝
          </button>
        </div>

        <div className="flex flex-col gap-2 lg:flex-row lg:flex-wrap lg:items-center">
          <div className="grid grid-cols-4 gap-1 rounded-xl bg-gray-100 p-1 dark:bg-[#0F172A] lg:flex lg:flex-wrap">
            {PLAYBACK_SPEEDS.map((speed) => (
              <button
                key={speed}
                type="button"
                onClick={() => onPlaybackRateChange(speed)}
                className={`rounded-lg px-2.5 py-1.5 text-xs font-semibold lg:py-1 ${
                  playbackRate === speed
                    ? "bg-blue-600 text-white"
                    : "bg-transparent text-gray-700 dark:text-gray-200"
                }`}
              >
                {speed.toFixed(1)}x
              </button>
            ))}
          </div>

          <button
            type="button"
            onClick={onDownload}
            title="조회한 시간대의 영상을 다운로드합니다 (끊긴 구간은 나눠서 저장)"
            className="rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700"
          >
            다운로드
          </button>
        </div>
      </div>
    </div>
  );
}